import { produce } from 'immer';
import { atom, selector } from 'recoil';

import { CarFilter, SpecCountType } from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const transmissionTypesState = atom<CarFilter[]>({
  key: 'transmissionTypes',
  default: [],
});

export const transmissionCountState = atom<SpecCountType[]>({
  key: 'transmissionCount',
  default: [],
});

export const transmissionTypeSearchOptionsSelector = selector({
  key: 'transmissionTypeSearchOptionsSelector',
  get: ({ get }) => {
    const transmissionTypes = get(transmissionTypesState);
    const transmissionTypeSearchOptions: CarFilter[] = transmissionTypes.filter(
      (option) => !option.isSelected,
    );

    return { transmissionTypeSearchOptions };
  },
});

export const toggleTransmissionTypeSelector = selector<number | null>({
  key: 'toggleTransmissionType',
  get: () => null,
  set: ({ set, get }, transmissionTypeId) => {
    const transmissionTypes = get(transmissionTypesState);

    const updatedTransmissionType = produce(
      transmissionTypes,
      (transmissionTypesDraft) => {
        if (transmissionTypeId) {
          const transmissionType = transmissionTypesDraft.find(
            (transmissionType) => transmissionType.id === transmissionTypeId,
          );

          transmissionType!.isSelected = !transmissionType?.isSelected;
          return;
        }
      },
    );

    set(transmissionTypesState, updatedTransmissionType);
  },
});

export const selectedTransmissionTypesSelector = selector<CarFilter[]>({
  key: 'selectedTransmissionTypes',
  get: ({ get }) => {
    const transmissionTypes = get(transmissionTypesState);

    return transmissionTypes.filter(
      (transmissionType) => transmissionType.isSelected,
    );
  },
});

export const hasSelectedTransmissionFilterSelector = selector<boolean>({
  key: 'hasSelectedTransmissionFilter',
  get({ get }) {
    return Boolean(get(selectedTransmissionTypesSelector).length);
  },
});

export const resetSelectedTransmissionTypesSelector = selector<number | null>({
  key: 'resetSelectedTransmissionTypes',
  get: () => null,
  set: ({ set, get }, transmissionTypeId) => {
    const transmissionTypes = get(transmissionTypesState);

    const updatedTransmissionType = produce(
      transmissionTypes,
      (transmissionTypesDraft) => {
        if (transmissionTypeId) {
          const transmissionType = transmissionTypesDraft.find(
            (transmissionType) => transmissionType.id === transmissionTypeId,
          );

          transmissionType!.isSelected = false;
          return;
        }

        transmissionTypesDraft.forEach((transmissionType) => {
          if (
            transmissionTypeId &&
            transmissionType.id === transmissionTypeId
          ) {
            transmissionType.isSelected = false;
            return;
          }
          transmissionType.isSelected = false;
        });
      },
    );

    set(transmissionTypesState, updatedTransmissionType);
  },
});

export const selectTransmissionTypeSelector = selector({
  key: 'selectTransmissionType',
  get: () => [] as any[],
  set: ({ set, get }, transmissionTypeIds) => {
    const transmissionTypes = get(transmissionTypesState);

    const updatedTransmissionType = produce(
      transmissionTypes,
      (transmissionTypesDraft) => {
        if (guardRecoilDefaultValue(transmissionTypeIds)) return;
        if (transmissionTypeIds.length) {
          transmissionTypeIds
            .map((transmissionTypeId) => Number(transmissionTypeId))
            .forEach((transmissionTypeId) => {
              const transmissionType = transmissionTypesDraft.find(
                (transmissionType) =>
                  transmissionType.id === transmissionTypeId,
              );

              if (!transmissionType) return;
              transmissionType.isSelected = true;
            });

          return;
        }
      },
    );

    set(transmissionTypesState, updatedTransmissionType);
  },
});

export const visibleTransmissionTypeSelector = selector({
  key: 'visibleTransmissionTypeSelector',
  get: ({ get }) => {
    const transmissionTypes = get(transmissionTypesState);
    const transmissionCount = get(transmissionCountState);

    return transmissionTypes.map((transmissionType) => ({
      ...transmissionType,
      isDisabled: !transmissionCount?.find(
        (group) => group?.specId === transmissionType?.id,
      )?.count,
    }));
  },
});
